import {
    AuthContext,
    DataTable,
    DataTablePresenter,
    IAuthContextProps,
    ITableColumn,
    ITableFetchOptions,
    ProviderCollectionInvoice,
    renderDate,
} from '@enter/core';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, parseSortBy, RootStore } from 'stores';
import { amountColumnSize, renderCollectionAmount, TableEmpty } from './patient-collections-cells';
import {
    collectionsSearchEmptyMessage,
    initialFiltersFromPatientIdSearch,
    syncPatientIdSearch,
} from './patient-collections-filters';
import * as S from './patient-collections.styles';

interface Props extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
    onOpen: (invoiceId: string) => void;
    patientName: (first?: string, last?: string) => string;
    patientIdSearch?: string;
}

interface State {
    loading: boolean;
}

const filterSchemas = [];

class PatientCollectionsReadyPage extends React.Component<Props, State> {
    private presenter: DataTablePresenter<ProviderCollectionInvoice>;
    private columns: ITableColumn<ProviderCollectionInvoice>[] = [];

    constructor(props: Props) {
        super(props);
        this.state = { loading: false };
        void (props.rootStore as RootStore);

        this.columns = [
            {
                id: 'Patient',
                Header: 'Patient',
                accessor: row => row,
                disableSortBy: true,
                Cell: ({ row }) => {
                    const inv = row.original;
                    const info = inv.invoicePatient?.info;
                    return (
                        <S.Cell>
                            <S.CellPrimary>
                                {props.patientName(info?.patientFirstName, info?.patientLastName)}
                            </S.CellPrimary>
                            <S.CellSecondary>
                                {[inv.controlNumber, inv.invoicePatient?.externalPatientId]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </S.CellSecondary>
                        </S.Cell>
                    );
                },
            },
            {
                id: 'Dos',
                Header: 'DOS',
                accessor: 'serviceDate',
                disableSortBy: true,
                Cell: ({ cell }) => <S.Cell>{renderDate(cell.value) || <S.Unknown>—</S.Unknown>}</S.Cell>,
            },
            {
                id: 'Payer',
                Header: 'Payer',
                accessor: 'primaryPayerName',
                disableSortBy: true,
                Cell: ({ cell }) => <S.Cell>{cell.value || <S.Unknown>—</S.Unknown>}</S.Cell>,
            },
            {
                id: 'Charge',
                Header: 'Charge',
                accessor: 'invoiceAmount',
                disableSortBy: true,
                ...amountColumnSize,
                Cell: ({ cell }) => <S.Cell>{renderCollectionAmount(cell.value)}</S.Cell>,
            },
            {
                id: 'Balance',
                Header: 'Balance Due',
                accessor: 'currentBalance',
                disableSortBy: true,
                ...amountColumnSize,
                Cell: ({ cell }) => (
                    <S.Cell>
                        <S.CellPrimary>{renderCollectionAmount(cell.value)}</S.CellPrimary>
                    </S.Cell>
                ),
            },
            {
                id: 'Confirmed',
                Header: 'Confirmed As',
                accessor: 'memoNotes',
                disableSortBy: true,
                Cell: ({ cell }) => (
                    <S.Cell>
                        <S.ConfirmedChip>{cell.value || 'Confirmed'}</S.ConfirmedChip>
                    </S.Cell>
                ),
            },
            {
                id: 'Delivery',
                Header: 'Delivery',
                disableSortBy: true,
                Cell: () => <S.Cell>Nightly batch</S.Cell>,
            },
        ];

        this.presenter = new DataTablePresenter<ProviderCollectionInvoice>({
            name: 'patient-collections-ready',
            data: [],
            columns: this.columns,
            filterSchemas,
            filters: initialFiltersFromPatientIdSearch(props.patientIdSearch),
        });
    }

    public componentDidUpdate(prevProps: Props): void {
        syncPatientIdSearch(this.presenter, this.props.patientIdSearch, prevProps.patientIdSearch);
    }

    public componentWillUnmount(): void {
        this.props.authContext.cancelGroup?.('patient-collections-ready');
    }

    public render() {
        return (
            <S.TableShell>
                <DataTable
                    presenter={this.presenter}
                    isLoading={this.state.loading}
                    hideFiltering
                    NotFoundComponent={() => (
                        <TableEmpty
                            message={collectionsSearchEmptyMessage(
                                this.props.patientIdSearch,
                                'No invoices queued right now.',
                            )}
                        />
                    )}
                    onFetch={this.onFetch}
                    onRowClick={row => {
                        const id = row.original.collectionInvoiceId;
                        if (id) this.props.onOpen(id);
                    }}
                />
            </S.TableShell>
        );
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        try {
            this.setState({ loading: true });
            this.props.authContext.cancelGroup?.('patient-collections-ready');
            this.props.authContext.setGroup?.('patient-collections-ready');
            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            const sortAndOrder = parseSortBy(sortBy, this.columns);
            const listing = await this.props.rootStore.collectionInvoiceStore.loadInvoiceListing(
                {
                    page: pageIndex,
                    pageSize,
                    ...sortAndOrder,
                    filters: validFilters,
                },
                'ready',
            );
            this.presenter.onDataUpdate(listing.items || []);
            this.presenter.onDataTotalChange(listing.total || 0);
        } catch (err) {
            this.props.dialogContext.showError(err);
        } finally {
            this.setState({ loading: false });
        }
    };
}

export const PatientCollectionsReady = inject('rootStore')(
    observer((props: Omit<Props, 'authContext' | 'dialogContext'> & IInjectedRootStore) => {
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        return (
            <PatientCollectionsReadyPage
                {...props}
                authContext={authContext}
                dialogContext={dialogContext}
            />
        );
    }),
);
